import { z } from "@hono/zod-openapi";

import { uuidV7Schema } from "./identifiers";
import { vaultSnapshotResponseSchema } from "./synchronization";

export const organizationRoleSchema = z.enum([
  "org_owner",
  "org_admin",
  "member",
]);
export const membershipStatusSchema = z.enum(["invited", "active", "disabled"]);
export const principalTypeSchema = z.enum(["human", "service"]);
export const vaultTypeSchema = z.enum(["company", "team", "personal"]);
export const vaultRoleSchema = z.enum(["viewer", "editor", "vault_admin"]);

const displayNameSchema = z.string().trim().min(1).max(100);

export const organizationSummarySchema = z
  .object({
    organization_id: uuidV7Schema,
    name: z.string(),
    slug: z.string(),
    lifecycle_state: z.enum(["active", "suspended", "archived"]),
    role: organizationRoleSchema,
    anyone_links_allowed: z.boolean(),
    billing_account_id: uuidV7Schema.nullable(),
    plan: z.enum(["solo", "teams", "partner", "platform"]).nullable(),
    allow_groups: z.boolean(),
    allow_team_vaults: z.boolean(),
  })
  .openapi("OrganizationSummary");

export const organizationListResponseSchema = z
  .object({ organizations: z.array(organizationSummarySchema) })
  .openapi("OrganizationListResponse");

export const organizationUpdateRequestSchema = z
  .object({
    name: displayNameSchema.optional(),
    anyone_links_allowed: z.boolean().optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined || value.anyone_links_allowed !== undefined,
  )
  .openapi("OrganizationUpdateRequest");

export const principalDirectoryEntrySchema = z
  .object({
    principal_id: uuidV7Schema,
    principal_type: principalTypeSchema,
    display_name: z.string(),
  })
  .openapi("PrincipalDirectoryEntry");

export const principalDirectoryResponseSchema = z
  .object({ principals: z.array(principalDirectoryEntrySchema) })
  .openapi("PrincipalDirectoryResponse");

export const currentPrincipalSchema = principalDirectoryEntrySchema
  .extend({ primary_email: z.string().nullable() })
  .openapi("CurrentPrincipal");

export const currentPrincipalUpdateRequestSchema = z
  .object({
    display_name: displayNameSchema.optional(),
    primary_email: z
      .string()
      .trim()
      .max(254)
      .pipe(z.email())
      .transform((value) => value.toLowerCase())
      .optional(),
  })
  .refine(
    (value) =>
      value.display_name !== undefined || value.primary_email !== undefined,
  )
  .openapi("CurrentPrincipalUpdateRequest");

export const memberInventorySchema = z
  .object({
    principal_id: uuidV7Schema,
    principal_type: principalTypeSchema,
    display_name: z.string(),
    primary_email: z.string().nullable(),
    role: organizationRoleSchema,
    status: membershipStatusSchema,
    disabled_at: z.iso.datetime().nullable(),
    personal_vault_id: uuidV7Schema.nullable(),
    personal_vault_archived: z.boolean().nullable(),
  })
  .openapi("MemberInventory");

export const memberListResponseSchema = z
  .object({ members: z.array(memberInventorySchema) })
  .openapi("MemberListResponse");

export const invitationTokenSchema = z
  .string()
  .regex(/^nlk_invite_[A-Za-z0-9_-]{43}$/u);

export const invitationCreateRequestSchema = z
  .object({
    email: z
      .string()
      .trim()
      .max(254)
      .pipe(z.email())
      .transform((value) => value.toLowerCase()),
    role: organizationRoleSchema.default("member"),
    expires_in_hours: z.number().int().min(1).max(168).default(72),
  })
  .openapi("InvitationCreateRequest");

export const invitationIssueSchema = z
  .object({
    invitation_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    email: z.email(),
    role: organizationRoleSchema,
    expires_at: z.iso.datetime(),
    token: invitationTokenSchema,
    email_sent: z.boolean().default(false),
  })
  .openapi("InvitationIssue");

export const invitationPreviewSchema = z
  .object({
    organization_name: z.string(),
    email: z.email(),
    expires_at: z.iso.datetime(),
  })
  .openapi("InvitationPreview");

export const invitationInventorySchema = z
  .object({
    invitation_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    email: z.email(),
    role: organizationRoleSchema,
    status: z.literal("pending"),
    expires_at: z.iso.datetime(),
  })
  .openapi("InvitationInventory");

export const invitationListResponseSchema = z
  .object({ invitations: z.array(invitationInventorySchema) })
  .openapi("InvitationListResponse");

export const invitationRevokeResponseSchema = z
  .object({
    invitation_id: uuidV7Schema,
    state: z.literal("revoked"),
  })
  .openapi("InvitationRevokeResponse");

export const invitationAcceptRequestSchema = z
  .object({
    token: invitationTokenSchema,
    display_name: displayNameSchema,
    primary_email: z
      .string()
      .trim()
      .max(254)
      .pipe(z.email())
      .transform((value) => value.toLowerCase()),
  })
  .openapi("InvitationAcceptRequest");

export const invitationAcceptResponseSchema = z
  .object({
    organization: organizationSummarySchema,
    personal_vault_id: uuidV7Schema,
  })
  .openapi("InvitationAcceptResponse");

export const memberRoleUpdateRequestSchema = z
  .object({
    role: organizationRoleSchema.optional(),
    display_name: displayNameSchema.optional(),
  })
  .refine(
    (value) => value.role !== undefined || value.display_name !== undefined,
  )
  .openapi("MemberRoleUpdateRequest");

export const offboardingDispositionSchema = z.enum([
  "disable_and_retain",
  "archive_and_revoke",
  "delegate_and_disable",
]);

export const memberDisableRequestSchema = z
  .object({
    disposition: offboardingDispositionSchema.default("disable_and_retain"),
    delegate_principal_ids: z.array(uuidV7Schema).max(20).default([]),
  })
  .superRefine((value, context) => {
    if (
      value.disposition === "delegate_and_disable" &&
      value.delegate_principal_ids.length === 0
    ) {
      context.addIssue({ code: "custom", message: "delegate_required" });
    }
    if (
      value.disposition !== "delegate_and_disable" &&
      value.delegate_principal_ids.length > 0
    ) {
      context.addIssue({ code: "custom", message: "delegates_not_allowed" });
    }
    if (
      new Set(value.delegate_principal_ids).size !==
      value.delegate_principal_ids.length
    ) {
      context.addIssue({ code: "custom", message: "duplicate_delegate" });
    }
  })
  .openapi("MemberDisableRequest");

export const memberDisableResponseSchema = z
  .object({
    organization_id: uuidV7Schema,
    principal_id: uuidV7Schema,
    status: z.literal("disabled"),
    disposition: offboardingDispositionSchema,
  })
  .openapi("MemberDisableResponse");

export const servicePrincipalCreateRequestSchema = z
  .object({
    display_name: displayNameSchema,
    description: z.string().trim().max(500).nullable().default(null),
  })
  .openapi("ServicePrincipalCreateRequest");

export const servicePrincipalUpdateRequestSchema = z
  .object({
    display_name: displayNameSchema.optional(),
    description: z.string().trim().max(500).nullable().optional(),
  })
  .refine(
    (value) =>
      value.display_name !== undefined || value.description !== undefined,
  )
  .openapi("ServicePrincipalUpdateRequest");

export const servicePrincipalSchema = z
  .object({
    principal_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    display_name: z.string(),
    description: z.string().nullable(),
    status: z.enum(["active", "disabled"]),
  })
  .openapi("ServicePrincipal");

export const vaultCreateRequestSchema = z
  .object({ name: displayNameSchema })
  .openapi("VaultCreateRequest");

export const vaultDestructiveRequestSchema = z
  .object({
    confirmation: z.literal("delete"),
  })
  .strict()
  .openapi("VaultDestructiveRequest");

export const vaultDestructiveResponseSchema = z
  .object({
    vault_id: uuidV7Schema,
    action: z.enum(["clear", "delete"]),
  })
  .openapi("VaultDestructiveResponse");

export const vaultUpdateRequestSchema = z
  .object({
    name: displayNameSchema.optional(),
    local_sync_allowed: z.boolean().optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined || value.local_sync_allowed !== undefined,
  )
  .openapi("VaultUpdateRequest");

export const vaultSummarySchema = z
  .object({
    vault_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    vault_type: vaultTypeSchema,
    name: z.string(),
    owner_principal_id: uuidV7Schema.nullable(),
    local_sync_allowed: z.boolean(),
    archived_at: z.iso.datetime().nullable(),
    applied_template_id: uuidV7Schema.nullable(),
    effective_role: vaultRoleSchema,
  })
  .openapi("VaultSummary");

export const vaultListResponseSchema = z
  .object({ vaults: z.array(vaultSummarySchema) })
  .openapi("VaultListResponse");

export const vaultMembershipSchema = z
  .object({
    vault_id: uuidV7Schema,
    principal_id: uuidV7Schema,
    role: vaultRoleSchema,
  })
  .openapi("VaultMembership");

export const vaultMembershipInventorySchema = vaultMembershipSchema
  .extend({
    principal_type: principalTypeSchema,
    display_name: z.string(),
    primary_email: z.string().nullable(),
    membership_status: membershipStatusSchema,
    granted_at: z.iso.datetime(),
    is_owner: z.boolean(),
  })
  .openapi("VaultMembershipInventory");

export const vaultMembershipListResponseSchema = z
  .object({ memberships: z.array(vaultMembershipInventorySchema) })
  .openapi("VaultMembershipListResponse");

export const vaultMembershipUpdateRequestSchema = z
  .object({ role: vaultRoleSchema })
  .openapi("VaultMembershipUpdateRequest");

export const vaultMembershipRevokeResponseSchema = z
  .object({
    vault_id: uuidV7Schema,
    principal_id: uuidV7Schema,
    state: z.literal("revoked"),
  })
  .openapi("VaultMembershipRevokeResponse");

export const personalVaultRestoreResponseSchema = z
  .object({ vault_id: uuidV7Schema, state: z.literal("active") })
  .openapi("PersonalVaultRestoreResponse");

export const organizationGroupCreateRequestSchema = z
  .object({ name: displayNameSchema })
  .openapi("OrganizationGroupCreateRequest");

export const organizationGroupUpdateRequestSchema = z
  .object({ name: displayNameSchema })
  .openapi("OrganizationGroupUpdateRequest");

export const organizationGroupSummarySchema = z
  .object({
    group_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    name: z.string(),
    member_count: z.number().int().nonnegative(),
    member_ids: z.array(uuidV7Schema),
    created_at: z.iso.datetime(),
    archived_at: z.iso.datetime().nullable(),
  })
  .openapi("OrganizationGroupSummary");

export const organizationGroupListResponseSchema = z
  .object({ groups: z.array(organizationGroupSummarySchema) })
  .openapi("OrganizationGroupListResponse");

export const organizationGroupMemberSchema = z
  .object({
    group_id: uuidV7Schema,
    principal_id: uuidV7Schema,
    principal_type: principalTypeSchema,
    display_name: z.string(),
    primary_email: z.string().nullable(),
    added_at: z.iso.datetime(),
  })
  .openapi("OrganizationGroupMember");

export const vaultGroupMembershipSchema = z
  .object({
    vault_id: uuidV7Schema,
    group_id: uuidV7Schema,
    group_name: z.string(),
    role: vaultRoleSchema,
    granted_at: z.iso.datetime(),
  })
  .openapi("VaultGroupMembership");

export const organizationGroupDetailSchema = organizationGroupSummarySchema
  .extend({
    members: z.array(organizationGroupMemberSchema),
    vault_grants: z.array(vaultGroupMembershipSchema),
  })
  .openapi("OrganizationGroupDetail");

export const organizationGroupArchiveResponseSchema = z
  .object({
    group_id: uuidV7Schema,
    state: z.literal("archived"),
  })
  .openapi("OrganizationGroupArchiveResponse");

export const vaultGroupMembershipListResponseSchema = z
  .object({ memberships: z.array(vaultGroupMembershipSchema) })
  .openapi("VaultGroupMembershipListResponse");

export const vaultGroupMembershipUpdateRequestSchema = z
  .object({ role: vaultRoleSchema })
  .openapi("VaultGroupMembershipUpdateRequest");

export const vaultGroupMembershipGrantSchema = z
  .object({
    vault_id: uuidV7Schema,
    group_id: uuidV7Schema,
    role: vaultRoleSchema,
  })
  .openapi("VaultGroupMembershipGrant");

export const vaultGroupMembershipRevokeResponseSchema = z
  .object({
    vault_id: uuidV7Schema,
    group_id: uuidV7Schema,
    state: z.literal("revoked"),
  })
  .openapi("VaultGroupMembershipRevokeResponse");

export type InvitationCreateRequest = z.infer<
  typeof invitationCreateRequestSchema
>;
export type InvitationAcceptRequest = z.infer<
  typeof invitationAcceptRequestSchema
>;
export type MemberDisableRequest = z.infer<typeof memberDisableRequestSchema>;
export type ServicePrincipalCreateRequest = z.infer<
  typeof servicePrincipalCreateRequestSchema
>;
export type VaultSummary = z.infer<typeof vaultSummarySchema>;
export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;
export type MemberInventory = z.infer<typeof memberInventorySchema>;
export type PrincipalDirectoryEntry = z.infer<
  typeof principalDirectoryEntrySchema
>;
export type CurrentPrincipal = z.infer<typeof currentPrincipalSchema>;
export type CurrentPrincipalUpdateRequest = z.infer<
  typeof currentPrincipalUpdateRequestSchema
>;
export type InvitationIssue = z.infer<typeof invitationIssueSchema>;
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;
export type InvitationInventory = z.infer<typeof invitationInventorySchema>;
export type InvitationRevokeResponse = z.infer<
  typeof invitationRevokeResponseSchema
>;
export type InvitationAcceptResponse = z.infer<
  typeof invitationAcceptResponseSchema
>;
export type MemberDisableResponse = z.infer<typeof memberDisableResponseSchema>;
export type ServicePrincipal = z.infer<typeof servicePrincipalSchema>;
export type VaultMembership = z.infer<typeof vaultMembershipSchema>;
export type VaultMembershipInventory = z.infer<
  typeof vaultMembershipInventorySchema
>;
export type VaultMembershipRevokeResponse = z.infer<
  typeof vaultMembershipRevokeResponseSchema
>;
export type PersonalVaultRestoreResponse = z.infer<
  typeof personalVaultRestoreResponseSchema
>;
export type OrganizationGroupSummary = z.infer<
  typeof organizationGroupSummarySchema
>;
export type OrganizationGroupDetail = z.infer<
  typeof organizationGroupDetailSchema
>;
export type OrganizationGroupMember = z.infer<
  typeof organizationGroupMemberSchema
>;
export type VaultGroupMembership = z.infer<typeof vaultGroupMembershipSchema>;
export type VaultGroupMembershipRevokeResponse = z.infer<
  typeof vaultGroupMembershipRevokeResponseSchema
>;

export const shareTargetTypeSchema = z.enum(["vault", "folder", "file"]);
export const shareScopeSchema = z.enum(["anyone", "organization"]);
export const shareTokenSchema = z
  .string()
  .regex(/^nlk_share_[A-Za-z0-9_-]{43}$/u);

export const contentShareCreateRequestSchema = z
  .object({
    target_type: shareTargetTypeSchema,
    target_entry_id: uuidV7Schema,
    scope: shareScopeSchema,
    expires_in_days: z
      .union([z.literal(7), z.literal(30), z.literal(90)])
      .optional(),
  })
  .openapi("ContentShareCreateRequest");

export const contentShareSchema = z
  .object({
    share_id: uuidV7Schema,
    organization_id: uuidV7Schema,
    vault_id: uuidV7Schema,
    vault_name: z.string(),
    vault_type: vaultTypeSchema,
    target_type: shareTargetTypeSchema,
    target_entry_id: uuidV7Schema,
    target_name: z.string(),
    scope: shareScopeSchema,
    token_prefix: z.string(),
    token: shareTokenSchema.optional(),
    expires_at: z.iso.datetime().nullable(),
    created_at: z.iso.datetime(),
    created_by_principal_id: uuidV7Schema,
    created_by_display_name: z.string(),
  })
  .openapi("ContentShare");

export const contentShareListResponseSchema = z
  .object({ shares: z.array(contentShareSchema) })
  .openapi("ContentShareListResponse");

export const contentShareRevokeResponseSchema = z
  .object({
    share_id: uuidV7Schema,
    state: z.literal("revoked"),
  })
  .openapi("ContentShareRevokeResponse");

export const publicShareMetadataSchema = z
  .object({
    scope: shareScopeSchema,
    target_type: shareTargetTypeSchema,
    target_name: z.string(),
    vault_name: z.string(),
    expires_at: z.iso.datetime().nullable(),
    created_by_display_name: z.string(),
  })
  .openapi("PublicShareMetadata");

export const publicShareViewSchema = z
  .object({
    share: publicShareMetadataSchema,
    snapshot: vaultSnapshotResponseSchema,
    selected_file_id: uuidV7Schema.nullable(),
  })
  .openapi("PublicShareView");

export type ContentShare = z.infer<typeof contentShareSchema>;
export type ContentShareCreateRequest = z.infer<
  typeof contentShareCreateRequestSchema
>;
export type PublicShareView = z.infer<typeof publicShareViewSchema>;
